import { Landmark, Package, ShieldCheck, ArrowRight } from "lucide-react";
import { useLang } from "@/LangContext";
import { Reveal, Chapter } from "@/components/Reveal";
import { scrollToId } from "@/lib/scroll";
import Donate from "@/components/Donate";
import DonorWall from "@/components/DonorWall";

export default function Contribute() {
  const { t } = useLang();

  const goContact = (interestIdx) => {
    if (interestIdx !== null && interestIdx !== undefined) {
      window.dispatchEvent(
        new CustomEvent("montalban:set-interest", { detail: interestIdx })
      );
    }
    scrollToId("contacto");
  };

  return (
    <section
      id="contribuir"
      data-testid="contribute-section"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Chapter number={t.contrib.chapter} label={t.contrib.label} />
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-8 max-w-3xl font-display text-3xl font-bold uppercase leading-[0.95] tracking-tight text-white sm:text-5xl">
            {t.contrib.title}
          </h2>
        </Reveal>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-400 sm:text-lg">
            {t.contrib.subtitle}
          </p>
        </Reveal>

        <div className="mt-16">
          <Reveal>
            <Donate />
          </Reveal>
        </div>

        <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Reveal className="h-full">
            <div
              data-testid="contribute-bank-card"
              className="flex h-full flex-col border border-amber-400/40 bg-gradient-to-br from-amber-400/[0.07] to-transparent p-7 sm:p-9"
            >
              <div className="flex h-12 w-12 items-center justify-center border border-amber-400/60 text-amber-400">
                <Landmark size={20} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white sm:text-3xl">
                {t.contrib.bankTitle}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-300">
                {t.contrib.bankDesc}
              </p>
              <ul className="mt-6 space-y-2.5 border-t border-white/10 pt-6">
                {t.contrib.bankFields.map((f) => (
                  <li
                    key={f}
                    className="flex items-center justify-between gap-4"
                  >
                    <span className="text-sm text-slate-300">{f}</span>
                    <span className="font-code text-xs tracking-[0.2em] text-slate-600">
                      ••••••
                    </span>
                  </li>
                ))}
              </ul>
              <button
                data-testid="bank-cta-button"
                onClick={() => goContact(null)}
                className="group mt-8 flex items-center justify-center gap-3 bg-amber-400 px-5 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-[#0A0C10] transition-colors hover:bg-amber-300"
              >
                {t.contrib.bankCta}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </Reveal>

          <Reveal delay={0.1} className="h-full">
            <div
              data-testid="contribute-kind-card"
              className="flex h-full flex-col border border-white/10 bg-[#121620] p-7 transition-colors duration-500 hover:border-amber-400/40 sm:p-9"
            >
              <div className="flex h-12 w-12 items-center justify-center border border-white/15 text-slate-300">
                <Package size={20} />
              </div>
              <h3 className="mt-6 font-display text-2xl font-bold uppercase text-white sm:text-3xl">
                {t.contrib.kindTitle}
              </h3>
              <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-400">
                {t.contrib.kindDesc}
              </p>
              <button
                data-testid="kind-cta-button"
                onClick={() => goContact(3)}
                className="group mt-8 flex items-center justify-center gap-3 border border-white/20 px-5 py-3.5 font-display text-sm font-bold uppercase tracking-[0.14em] text-white transition-colors hover:border-amber-400 hover:text-amber-400"
              >
                {t.contrib.kindCta}
                <ArrowRight
                  size={16}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </button>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div
            data-testid="contribute-audit-note"
            className="mt-10 flex items-start gap-4 border border-white/10 bg-[#0C0F16] p-6"
          >
            <ShieldCheck size={22} className="mt-0.5 shrink-0 text-amber-400" />
            <p className="text-sm leading-relaxed text-slate-300">
              {t.contrib.auditNote}
            </p>
          </div>
        </Reveal>

        <DonorWall />
      </div>
    </section>
  );
}
